import { Link, Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
    LayoutDashboard,
    Users,
    CreditCard,
    CalendarDays,
    Wallet,
    Building2,
    Settings,
    Crown,
    Banknote,
    List,
    HeartPulse,
    Quote,
    Smile,
    Trophy,
    LogOut,
    Menu,
    X,
    Wrench,
    Timer,
    ChevronDown,
    Copy,
    Check,
    Store,
    ShoppingCart,
    Package,
    CircleDollarSign,
    Bell,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { useGym } from '@/contexts/GymContext';

const navGroups = [
    {
        key: 'alumnos',
        label: 'Alumnos',
        icon: Users,
        items: [
            { to: '/panel/clientes', icon: List, label: 'Lista' },
            { to: '/panel/tarjetas', icon: CreditCard, label: 'Tarjetas' },
            { to: '/panel/pagos', icon: Wallet, label: 'Pagos', alertKey: 'payment-promises' },
            { to: '/panel/planes-alumnos', icon: CircleDollarSign, label: 'Planes' },
            { to: '/panel/mediciones', icon: HeartPulse, label: 'Mediciones' },
            { to: '/panel/rankings', icon: Trophy, label: 'Ranking' },
        ],
    },
    {
        key: 'clases',
        label: 'Clases',
        icon: CalendarDays,
        items: [
            { to: '/panel/clases', icon: List, label: 'Lista' },
            { to: '/panel/satisfaccion', icon: Smile, label: 'Satisfacción' },
        ],
    },
    {
        key: 'herramientas',
        label: 'Herramientas',
        icon: Wrench,
        items: [
            { to: '/panel/timer', icon: Timer, label: 'Timer' },
        ],
    },
    {
        key: 'configuraciones',
        label: 'Configuraciones',
        icon: Settings,
        items: [
            { to: '/panel/gimnasio', icon: Building2, label: 'Gimnasio' },
            { to: '/panel/gimnasios', icon: Building2, label: 'Mis Gimnasios' },
            { to: '/panel/configuracion-pagos', icon: Banknote, label: 'Métodos de Pago' },
            { to: '/panel/frases', icon: Quote, label: 'Frases' },
            { to: '/panel/notificaciones', icon: Bell, label: 'Notificaciones' },
            { to: '/panel/suscripciones', icon: Crown, label: 'Suscripciones', alertKey: 'subscription' },
        ],
    },
    {
        key: 'tienda',
        label: 'Tienda',
        icon: Store,
        items: [
            { to: '/panel/tienda/productos', icon: ShoppingCart, label: 'Productos' },
            { to: '/panel/tienda/pedidos', icon: Package, label: 'Pedidos' },
        ],
    },
];

export default function OwnerLayout() {
    const { user, logout } = useAuth();
    const { selectedGym, gyms, setSelectedGym } = useGym();
    const navigate = useNavigate();
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [openGroups, setOpenGroups] = useState(() => new Set(navGroups.filter(g => g.items.some(i => location.pathname.startsWith(i.to))).map(g => g.key)));
    const [hasSubscriptionAlert, setHasSubscriptionAlert] = useState(false);
    const [hasPromisesAlert, setHasPromisesAlert] = useState(false);
    const [codeCopied, setCodeCopied] = useState(false);

    const copyGymCode = async () => {
        const code = selectedGym?.invite_token;
        if (!code) return;
        try {
            await navigator.clipboard.writeText(code);
        } catch {
            const textarea = document.createElement('textarea');
            textarea.value = code;
            document.body.appendChild(textarea);
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
        }
        setCodeCopied(true);
        setTimeout(() => setCodeCopied(false), 2000);
    };

    useEffect(() => {
        const activeGroups = new Set(navGroups
            .filter(g => g.items.some(i => location.pathname.startsWith(i.to)))
            .map(g => g.key));
        setOpenGroups(prev => new Set([...prev, ...activeGroups]));
    }, [location.pathname]);

    useEffect(() => {
        const checkSubscription = async () => {
            try {
                const res = await api.get('/owner/current-subscription');
                const subscription = res.data.data.subscription;
                const isActive = subscription?.status === 'active';
                const isExpired = subscription?.ends_at && new Date(subscription.ends_at) < new Date();
                const isLifetime = subscription?.is_lifetime;
                const hasActive = isActive && (!isExpired || isLifetime);
                setHasSubscriptionAlert(!hasActive);

                // If subscription is expired/inactive, force to subscription page
                if (!hasActive && location.pathname !== '/panel/suscripciones') {
                    navigate('/panel/suscripciones', { replace: true });
                    return;
                }

                // Only check gym count if subscription is active
                if (hasActive && gyms.length === 0 && location.pathname !== '/panel/crear-gimnasio') {
                    navigate('/panel/crear-gimnasio', { replace: true });
                }
            } catch {
                setHasSubscriptionAlert(true);
                if (location.pathname !== '/panel/suscripciones') {
                    navigate('/panel/suscripciones', { replace: true });
                }
            }
        };
        checkSubscription();
    }, [location.pathname, gyms.length, navigate]);

    useEffect(() => {
        const checkPromises = async () => {
            try {
                const [cash, transfer] = await Promise.all([
                    api.get('/owner/cash-promises'),
                    api.get('/owner/transfer-promises'),
                ]);
                setHasPromisesAlert(((cash.data.data?.length || 0) + (transfer.data.data?.length || 0)) > 0);
            } catch {
                setHasPromisesAlert(false);
            }
        };
        checkPromises();
    }, [location.pathname]);

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    return (
        <div className="min-h-screen flex bg-black">
            {sidebarOpen && (
                <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
            )}

            <aside className={cn(
                "fixed inset-y-0 left-0 z-50 w-64 bg-black border-r border-[#3a3a3f] transform transition-transform lg:translate-x-0 lg:static lg:z-auto",
                sidebarOpen ? "translate-x-0" : "-translate-x-full"
            )}>
                <div className="flex flex-col h-full">
                    <div className="flex items-center justify-between p-4">
                        <h1 className="text-xl font-bold text-white uppercase tracking-wide">
                            <Link to="/" onClick={() => setSidebarOpen(false)}>GYMS.ar</Link>
                        </h1>
                        <Button variant="ghost" size="icon" className="lg:hidden text-white" onClick={() => setSidebarOpen(false)}>
                            <X className="h-5 w-5" />
                        </Button>
                    </div>
                    <div className="px-4 pb-2">
                        {selectedGym?.invite_token && (
                            <button
                                type="button"
                                onClick={copyGymCode}
                                title="Copiar código de invitación"
                                className="group flex items-center gap-1.5 text-xs uppercase tracking-wider text-white/60 hover:text-white transition-colors"
                            >
                                <span>Código GYM: <span className="font-mono font-bold text-white">{selectedGym.invite_token}</span></span>
                                {codeCopied
                                    ? <Check className="h-3.5 w-3.5 text-white" />
                                    : <Copy className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />}
                            </button>
                        )}
                    </div>
                    <Separator className="bg-[#3a3a3f]" />
                    {gyms.length > 0 && (
                        <div className="px-4 py-3">
                            <label className="text-[10px] uppercase tracking-widest text-white/40 mb-1.5 block">Gimnasio activo</label>
                            <div className="relative">
                                <Building2 className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-white/50 pointer-events-none" />
                                <select
                                    className="w-full h-9 pl-8 pr-7 text-sm font-medium text-white bg-[#0a0a0a] border border-[#3a3a3f] rounded focus:outline-none focus:ring-1 focus:ring-white appearance-none cursor-pointer"
                                    value={selectedGym?.id ?? ''}
                                    onChange={(e) => {
                                        const gym = gyms.find(g => String(g.id) === e.target.value);
                                        if (gym) {
                                            setSelectedGym(gym);
                                            setSidebarOpen(false);
                                            navigate('/panel');
                                        }
                                    }}
                                >
                                    {gyms.map(g => (
                                        <option key={g.id} value={g.id} className="bg-[#0a0a0a] text-white">
                                            {g.name}
                                        </option>
                                    ))}
                                </select>
                                <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                                    <svg className="h-3.5 w-3.5 text-white/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    )}
                    <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                        <NavLink
                            to="/panel"
                            end
                            onClick={() => setSidebarOpen(false)}
                            className={({ isActive }) => cn(
                                "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                                isActive
                                    ? "bg-white/10 text-white"
                                    : "text-white/60 hover:bg-white/5"
                            )}
                        >
                            <LayoutDashboard className="h-4 w-4" />
                            <span className="flex-1 uppercase tracking-wider">Dashboard</span>
                        </NavLink>

                        {navGroups.map(group => (
                            <div key={group.key} className="mt-2">
                                <button
                                    type="button"
                                    onClick={() => setOpenGroups(prev => {
                                        const next = new Set(prev);
                                        if (next.has(group.key)) {
                                            next.delete(group.key);
                                        } else {
                                            next.add(group.key);
                                        }
                                        return next;
                                    })}
                                    className={cn(
                                        "w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors text-white/60 hover:bg-white/5"
                                    )}
                                >
                                    <group.icon className="h-4 w-4" />
                                    <span className="flex-1 uppercase tracking-wider text-left">{group.label}</span>
                                    <ChevronDown className={cn("h-4 w-4 transition-transform", openGroups.has(group.key) ? "rotate-180" : "")} />
                                </button>
                                {openGroups.has(group.key) && (
                                    <div className="mt-1 space-y-1 pl-4">
                                        {group.items.map(item => (
                                            <NavLink
                                                key={item.to}
                                                to={item.to}
                                                end={item.end}
                                                onClick={() => setSidebarOpen(false)}
                                                className={({ isActive }) => cn(
                                                    "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                                                    isActive
                                                        ? "bg-white/10 text-white"
                                                        : "text-white/60 hover:bg-white/5"
                                                )}
                                            >
                                                <item.icon className="h-4 w-4" />
                                                <span className="flex-1 uppercase tracking-wider">{item.label}</span>
                                                {item.alertKey === 'subscription' && hasSubscriptionAlert && (
                                                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-black bg-white rounded-full">
                                                        1
                                                    </span>
                                                )}
                                                {item.alertKey === 'payment-promises' && hasPromisesAlert && (
                                                    <span className="relative flex h-2.5 w-2.5">
                                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-400 opacity-75" />
                                                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-yellow-400" />
                                                    </span>
                                                )}
                                            </NavLink>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </nav>
                    <Separator className="bg-[#3a3a3f]" />
                    <div className="p-4 space-y-2">
                        <div className="flex items-center justify-between">
                            <div className="min-w-0">
                                <p className="text-sm font-medium truncate text-white">{user?.name}</p>
                                <p className="text-xs text-white/60 truncate">{user?.email}</p>
                            </div>
                        </div>
                        <Button variant="outline" size="sm" className="w-full justify-start text-white border-[#3a3a3f] hover:bg-white/5 hover:text-white" onClick={handleLogout}>
                            <LogOut className="h-4 w-4 mr-2" />
                            Cerrar sesión
                        </Button>
                    </div>
                </div>
            </aside>

            <div className="flex-1 flex flex-col min-w-0">
                <header className="sticky top-0 z-30 bg-black border-b border-[#3a3a3f] px-4 py-3 flex items-center justify-between lg:hidden">
                    <div className="flex items-center">
                        <Button variant="ghost" size="icon" className="text-white" onClick={() => setSidebarOpen(true)}>
                            <Menu className="h-5 w-5" />
                        </Button>
                        <h1 className="ml-3 text-lg font-semibold text-white uppercase tracking-wide">
                            <Link to="/">GYMS.ar</Link>
                        </h1>
                    </div>
                </header>
                <main className="flex-1 p-4 md:p-6 lg:p-8">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
